import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../api/axiosInstance";

function AdminOccupancyAuditDiffPopup({ isOpen, context, onClose, onRebuildSuccess }) {
  const [contentReady, setContentReady] = useState(false);
  const [rows, setRows] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [rebuildingReservationId, setRebuildingReservationId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setContentReady(false);

    async function loadEverything() {
      if (!context) {
        setRows([]);
        setErrorMsg("Missing context.");
        return;
      }

      const params = {
        business_unit_id: Number(context.business_unit_id),
        room_category_id: Number(context.room_category_id),
        stay_date: String(context.stay_date || ""),
      };

      try {
        setErrorMsg("");
        const resp = await axios.get("/api/admin/occupancy-audit/details", { params });
        const list = Array.isArray(resp?.data?.reservations) ? resp.data.reservations : [];
        console.log("[OCC_AUDIT][UI] loaded details", {
          params,
          count: list.length,
          sample: list[0] || null,
        });
        setRows(list);
      } catch (err) {
        console.error("[OCC_AUDIT][UI] failed loading details", err);
        setRows([]);
        setErrorMsg(err?.response?.data?.error || "Failed to load occupancy details.");
      }
    }

    async function load() {
      await loadEverything();
      setContentReady(true);
    }

    load();
  }, [isOpen, context]);

  if (!isOpen) return null;
  if (!contentReady) return null;

  async function handleRebuild(reservation) {
    const reservation_id = Number(reservation?.reservation_id);
    if (!reservation_id) return;

    const payload = { reservation_id };
    if (context?.stay_date) payload.stay_date = context.stay_date;

    setRebuildingReservationId(reservation_id);
    setErrorMsg("");

    try {
      console.log("[OCC_AUDIT][UI] rebuild click", payload);
      const resp = await axios.post("/api/admin/forecast-occupancy/rebuild", payload);
      console.log("[OCC_AUDIT][UI] rebuild result", resp?.data || null);

      if (typeof onRebuildSuccess === "function") {
        await onRebuildSuccess(resp?.data || null);
      }
    } catch (err) {
      console.error("[OCC_AUDIT][UI] rebuild failed", err);
      setErrorMsg(err?.response?.data?.error || "Failed to rebuild forecast.");
    } finally {
      setRebuildingReservationId(null);
    }
  }

  const hasRows = rows.length > 0;

  const popupNode = (
    <div
      className="inv-popup-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        backgroundColor: "rgba(0, 0, 0, 0.48)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
    >
      <div
        className="inv-popup-container bg-dark text-light p-4 rounded"
        style={{
          width: "920px",
          maxWidth: "92vw",
          maxHeight: "calc(100vh - 96px)",
          overflowY: "auto",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="text-warning mb-0">Occupancy Audit Details</h5>
          <button type="button" className="btn btn-outline-light btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="small text-white-50 mb-3">
          <div>Business Unit: {context?.business_unit_name || context?.business_unit_id || "-"}</div>
          <div>Room Category: {context?.room_category_name || context?.room_category_id || "-"}</div>
          <div>Stay Date: {context?.stay_date || "-"}</div>
        </div>

        {errorMsg ? (
          <div className="alert alert-danger py-2 mb-3">{errorMsg}</div>
        ) : null}

        <div className="table-responsive">
          <table
            className="table table-dark table-striped table-sm align-middle mb-0"
            style={{
              tableLayout: "fixed",
              width: "100%",
              fontSize: "0.74rem",
              lineHeight: "1.05rem",
            }}
          >
            <thead>
              <tr>
                <th className="text-center" style={{ width: "11%" }}>Reservation</th>
                <th className="text-center" style={{ width: "25%" }}>Locator / External</th>
                <th className="text-center" style={{ width: "10%" }}>Status</th>
                <th className="text-center" style={{ width: "20%" }}>Dates / Nights</th>
                <th className="text-center" style={{ width: "10%" }}>Guests</th>
                <th className="text-center" style={{ width: "8%" }}>Rate</th>
                <th className="text-center" style={{ width: "8%" }}>Forecast</th>
                <th className="text-center" style={{ width: "8%" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {!hasRows ? (
                <tr>
                  <td colSpan={8} className="text-center text-white-50">
                    No reservations found for this night.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.reservation_id}>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      {row.reservation_id}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <div>{row.locator || "-"}</div>
                      <div className="text-white-50">{row.external_reservation_id || "-"}</div>
                      <div className="text-white-50">{row.external_partner_locator || "-"}</div>
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      {row.status || "-"}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <div>{row.check_in_date || "-"} to {row.check_out_date || "-"}</div>
                      <div className="text-white-50">Nights: {row.room_nights ?? 0}</div>
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <div>A {row.adults ?? 0}</div>
                      <div>C {row.children ?? 0}</div>
                      <div>B {row.babies ?? 0}</div>
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      {row.rate_id ?? "-"}
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <div>{row.has_forecast_for_that_day ? "Yes" : "No"}</div>
                      <div className="text-white-50">{row.forecast_rows_for_that_day ?? 0} rows</div>
                    </td>
                    <td className="text-center" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-warning p-0"
                        onClick={() => handleRebuild(row)}
                        disabled={rebuildingReservationId === row.reservation_id}
                      >
                        {rebuildingReservationId === row.reservation_id ? "Rebuilding..." : "Rebuild forecast"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return createPortal(popupNode, document.body);
}

export default AdminOccupancyAuditDiffPopup;
